import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { useHistory } from "react-router";

import { StatusCode } from "api/protocols";
import {
  GetTeesheetBookingFee,
  IPostTeesheetBookingFee,
  PostTeesheetBookingFee,
  TeesheetBookingFee,
} from "api/rpc/2024-04/facilityAdmin/teesheet/bookingFee";

import { showError, showSuccess } from "redux/actions/ui";

import { useAppDispatch } from "hooks/redux";
import { isEmpty } from "helpers/Helpers";
import { LocaleCurrency } from "helpers/Locale";

import Page from "components/page/Page";
import Sheet from "components/sheet/Sheet";
import { Badge } from "components/badge/Badge";
import DataTable from "components/dataTable/DataTable";
import Portal from "elements/Portal";

import BookingFeeFormFields from "./components/BookingFeeFormFields";
import BookingFeeCustomerTypesModal from "./components/BookingFeeCustomerTypesModal";
import { initialBookingFeeFormValues, isBookingFeeFormValid, TBookingFeeFormValues } from "./bookingFeeForm";
import { useBookingFeeCustomerTypes } from "./hooks/useBookingFeeCustomerTypes";

import "./bookingFees.scss";

const TABLE_LIMIT = 10;

export default function BookingFees() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const [teesheetBookingFees, setTeesheetBookingFees] = useState<TeesheetBookingFee[]>(undefined);
  const [tableOffset, setTableOffset] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCustomerTypesModalOpen, setIsCustomerTypesModalOpen] = useState(false);
  const [formValues, setFormValues] = useState<TBookingFeeFormValues>(initialBookingFeeFormValues);

  const customerTypes = useBookingFeeCustomerTypes();

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTeesheetBookingFees(source.token);
    return () => {
      source.cancel();
    };
  }, []);

  const paginatedFees = useMemo(() => {
    if (!teesheetBookingFees) {
      return [];
    }

    return teesheetBookingFees.slice(tableOffset, tableOffset + TABLE_LIMIT);
  }, [tableOffset, teesheetBookingFees]);

  async function loadTeesheetBookingFees(token?: CancelToken) {
    if (teesheetBookingFees) {
      setTeesheetBookingFees(undefined);
    }

    const res = await GetTeesheetBookingFee(null, !token, token);

    if (token?.reason) {
      return;
    }

    if (Number(res.status) !== Number(StatusCode.OK)) {
      setTeesheetBookingFees([]);
      return;
    }

    setTeesheetBookingFees(res.data ?? []);
  }

  async function saveTeesheetBookingFee() {
    const variantId = formValues.variant?.id;
    if (!variantId) {
      return;
    }

    const params: IPostTeesheetBookingFee = {
      variant_id: variantId,
      non_refundable: formValues.non_refundable ?? false,
      days_in_advance_start: parseInt(formValues.days_in_advance_start ?? "", 10),
      days_in_advance_end: parseInt(formValues.days_in_advance_end ?? "", 10),
      customer_type_ids: formValues.customerTypeIds ?? [],
    };

    const res = await PostTeesheetBookingFee(params, true);

    if (Number(res.status) !== Number(StatusCode.OK)) {
      dispatch(showError(t("Something went wrong.  Unable to save new booking fee.")));
      return;
    }

    if (res.data) {
      setTeesheetBookingFees(prev => [...(prev ?? []), res.data]);
    }
    dispatch(showSuccess(t("New booking fee has been added successfully.")));
    closeBookingFeeNewModal();
  }

  function closeBookingFeeNewModal() {
    setIsCreateModalOpen(false);
    setIsCustomerTypesModalOpen(false);
    setFormValues(initialBookingFeeFormValues);
    customerTypes.reset();
  }

  function openCreateModal() {
    setFormValues(initialBookingFeeFormValues);
    customerTypes.reset();
    setIsCreateModalOpen(true);
  }

  function handleCustomerTypesApply(selectedIds: number[]) {
    setFormValues(prev => ({ ...prev, customerTypeIds: selectedIds }));
    customerTypes.setSelection(selectedIds);
    closeCustomerTypesModal();
  }

  function closeCustomerTypesModal() {
    setIsCustomerTypesModalOpen(false);
    customerTypes.setSearch("");
  }

  function handleRemoveCustomerType(id: number) {
    const nextIds = (formValues.customerTypeIds ?? []).filter(typeId => typeId !== id);
    setFormValues(prev => ({ ...prev, customerTypeIds: nextIds }));
    customerTypes.setSelection(nextIds);
  }

  function handleTableNavigation(direction: "prev" | "next") {
    setTableOffset(prev => (direction === "prev" ? Math.max(0, prev - TABLE_LIMIT) : prev + TABLE_LIMIT));
  }

  const primaryAction = {
    content: t("New booking fee"),
    action: openCreateModal,
  };

  return (
    <>
      <Page
        title={t("Booking fees")}
        breadcrumbs={ [
          { label: t("Booking fees"), url: "/admin/settings/tee-sheet/booking-fees" },
        ]}
        primaryAction={primaryAction}
      >
        <DataTable
          columns={[
            { label: t("Fee product name") },
            { label: t("Price per player") },
            { label: t("Booking window") },
            { label: t("Refund policy") },
          ]}
          loading={teesheetBookingFees === undefined}
          footer={{
            tableLimit: TABLE_LIMIT,
            tableOffset,
            disableNextOffset: !teesheetBookingFees || tableOffset + TABLE_LIMIT >= teesheetBookingFees.length,
            handleTableOffset: handleTableNavigation,
          }}
        >
          {paginatedFees.map(fee => (
            <tr
              key={fee.id}
              className="clickable"
              onClick={() => history.push("/admin/settings/tee-sheet/booking-fee/" + String(fee.id))}
            >
              <td>{fee?.product_title ?? ""}</td>
              <td>
                <LocaleCurrency amount={fee?.price ?? 0} /> / {fee?.unit ?? ""}
              </td>
              <td>
                {fee?.days_in_advance_start ?? ""} - {fee?.days_in_advance_end ?? ""} {t("days in advance")}
              </td>
              {!isEmpty(fee?.non_refundable) ? (
                <td>
                  <Badge type={fee?.non_refundable ? "warning" : "success"} size="medium">
                    {fee?.non_refundable ? t("Non-refundable") : t("Refundable")}
                  </Badge>
                </td>
              ) : (
                <td>
                  <p>{t("N/A")}</p>
                </td>
              )}
            </tr>
          ))}
        </DataTable>
      </Page>

      <Portal isMounted={isCreateModalOpen}>
        <Sheet
          open={isCreateModalOpen}
          onCancel={closeBookingFeeNewModal}
          title={t("New booking fee")}
          supportText={t("Choose a product and booking window to add a booking fee.")}
          closable
          onOk={saveTeesheetBookingFee}
          size="small"
          okText={t("Add booking fee")}
          okDisabled={!isBookingFeeFormValid(formValues)}
          overflow
        >
          <BookingFeeFormFields
            values={formValues}
            onChange={setFormValues}
            productSearchEnabled={isCreateModalOpen}
            selectedCustomerTypes={customerTypes.selectedTypes}
            onSelectCustomerTypes={() => setIsCustomerTypesModalOpen(true)}
            onRemoveCustomerType={handleRemoveCustomerType}
            onCreateProduct={() => history.push("/admin/product/new")}
          />
        </Sheet>
      </Portal>

      <BookingFeeCustomerTypesModal
        isOpen={isCustomerTypesModalOpen}
        selectedIds={formValues.customerTypeIds}
        search={customerTypes.search}
        isLoading={customerTypes.isLoading}
        filteredTypes={customerTypes.filteredTypes}
        onSearchChange={customerTypes.setSearch}
        onApply={handleCustomerTypesApply}
        onClose={closeCustomerTypesModal}
        onCreateCustomerType={() => history.push("/admin/settings/customer_types/new")}
        onSort={customerTypes.sortTypes}
      />
    </>
  );
}
